import React, { useState, useEffect } from 'react';
import { Modal, Select, DatePicker, Input, Button, Typography, Checkbox, Form } from 'antd';
import { UserPlus, Calendar, MessageSquare, X } from 'lucide-react';
import dayjs from 'dayjs';
import dialerService from '../services/dialerService';
import { getSession } from '../../../getSession';

const { Text } = Typography;
const { TextArea } = Input;

const FollowupModalV2 = ({ isOpen, onClose, onSave, record, users = [] }) => {
  const [form] = Form.useForm();
  const [followupStatus, setFollowupStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    { label: 'Call-Back', value: '1' },
    { label: 'Not-Interested', value: '2' },
    { label: 'Dead Lead', value: '3' },
    { label: 'Wrong Number', value: '4' },
    { label: 'Converted', value: '5' },
    { label: 'Call-Back Converted', value: '6' },
  ];

  useEffect(() => {
    if (isOpen && record) {
      form.resetFields();
      form.setFieldsValue({
        followupDate: dayjs().add(1, 'day'),
        assignedTo: users[0]?.value,
        followupStatus: '1'
      });
      setFollowupStatus('1');
    }
  }, [isOpen, users, form, record]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      const { userId, parentId } = getSession();
      const followupData = {
        LeadId: record.Id,
        FollowupStatus: values.followupStatus,
        NextFollowupDate: values.followupDate ? values.followupDate.format('DD-MMM-YYYY HH:mm') : '',
        AssignedTo: values.assignedTo || userId,
        Remarks: values.remarks,
        ReAssign: !!values.reAssign,
        UserId: userId,
        ParentId: parentId,
        EntityType: record.EntityName || 'Lead'
      };

      const res = await dialerService.addFollowup(followupData, ''); // dltTemplateId empty for now
      if (res.Details) alert(res.Details);
      onSave(followupData);
      onClose();
    } catch (error) {
      console.error('Failed to add followup:', error);
      alert('Failed to add followup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const showCallBackFields = ['1', '5', '6'].includes(followupStatus);

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <UserPlus size={20} className="text-blue-600" />
          <span className="text-lg font-bold text-slate-800">Add Follow-Up</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={500}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          followupStatus: '1',
        }}
        className="py-4"
      >
        <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl mb-6">
          <Text className="text-blue-800 font-medium block">
            Adding followup for: <span className="font-bold">{record?.PersonName || record?.ScheduleMobileNo}</span>
          </Text>
        </div>

        <Form.Item
          name="followupStatus"
          label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Followup Status</span>}
          rules={[{ required: true, message: 'Please select status' }]}
        >
          <Select 
            placeholder="Select Status" 
            onChange={setFollowupStatus}
            options={statusOptions}
            className="h-11"
            getPopupContainer={trigger => trigger.parentNode}
          />
        </Form.Item>

        {showCallBackFields && (
          <>
            <Form.Item
              name="assignedTo"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Assign To</span>}
              rules={[{ required: true, message: 'Please select user' }]}
            >
              <Select 
                placeholder="Select User" 
                options={users}
                className="h-11"
                getPopupContainer={trigger => trigger.parentNode}
              />
            </Form.Item>

            <Form.Item
              name="followupDate"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Next Followup Date & Time</span>}
              rules={[{ required: true, message: 'Please select date' }]}
            >
              <DatePicker 
                showTime 
                format="DD-MMM-YYYY HH:mm" 
                className="w-full h-11"
                getPopupContainer={trigger => trigger.parentNode}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="remarks"
          label={<span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Remarks</span>}
          rules={[{ required: true, message: 'Please enter remarks' }]}
        >
          <TextArea rows={3} placeholder="Enter followup details..." className="rounded-xl" />
        </Form.Item>

        {showCallBackFields && (
          <Form.Item name="reAssign" valuePropName="checked">
            <Checkbox className="text-slate-600 font-medium">Re-Assign this record</Checkbox>
          </Form.Item>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <Button onClick={onClose} className="rounded-xl font-bold h-11 px-6">
            Cancel
          </Button>
          <Button 
            type="primary" 
            htmlType="submit"
            className="rounded-xl font-bold h-11 px-10 bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200 border-none"
          >
            Save Followup
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default FollowupModalV2;
